/**
 * Trend path renderer.
 * Draws the main trend line and gradient area underneath.
 */

import * as d3 from 'd3';

import type { DataPoint } from '../types';

import type { SVGSelection, YScale, GetXFn, SelectionsCache } from './types';

/**
 * Draws the main trend line and the gradient area underneath.
 *
 * @param svg - D3 SVG selection
 * @param data - Array of data points to render
 * @param getX - Function to convert timestamp to X coordinate
 * @param yScale - Y-axis scale function
 * @param height - Chart height in pixels
 * @param color - Current trend color
 * @param cache - Optional cached D3 selections (avoids DOM queries)
 */
export const renderTrendPath = (
    svg: SVGSelection,
    data: DataPoint[],
    getX: GetXFn,
    yScale: YScale,
    height: number,
    color: string,
    cache?: SelectionsCache,
): void => {
    const lineGenerator = d3
        .line<DataPoint>()
        .x((d) => getX(d.timestamp))
        .y((d) => yScale(d.value))
        .curve(d3.curveMonotoneX);

    const dPath = lineGenerator(data);

    // Use cached selections if available, otherwise query DOM
    const linePath = cache?.linePath ?? svg.select('.line-path');
    const linePathCore = cache?.linePathCore ?? svg.select('.line-path-core');
    const areaPath = cache?.areaPath ?? svg.select('.area-path');
    const gradientStop =
        cache?.gradientStop ?? svg.select('#line-gradient stop:nth-child(1)');

    if (dPath) {
        // 1. Outer glow stroke (neon color with bloom filter)
        linePath.attr('d', dPath).attr('stroke', color);

        // 2. Inner core stroke (white-hot center)
        linePathCore.attr('d', dPath);

        // 3. Area (Gradient fill with scanline mask)
        const lastPt = data[data.length - 1];
        const firstPt = data[0];
        if (firstPt && lastPt) {
            const areaPathData = `${dPath} L ${getX(lastPt.timestamp)} ${height} L ${getX(firstPt.timestamp)} ${height} Z`;
            areaPath.attr('d', areaPathData);
        }
    }

    // Update gradient color to match trend
    gradientStop.attr('stop-color', color);
};
